import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  type FormActionData,
  InfoPanel,
  Paragraph,
  Section,
  filterToPostGraphileFilter,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  CustomizableServicesDocument,
  type CustomizableServicesQuery,
  HostingServiceConfigurationsDocument,
  type HostingServiceConfigurationsQuery,
  ProviderType,
  ReadyServiceDeploymentsDocument,
  type ReadyServiceDeploymentsQuery,
  ResourceStatus,
  type ServiceConfiguration,
  type ServiceDeploymentFilter,
  StorageAccountDocument,
  type StorageAccountQuery,
} from '../../../../generated/hosting-service';
import { EnvironmentServiceState } from '../../../../generated/id-service-env-administration';
import {
  formatEnumValueToTitleCase,
  getManagedServiceData,
  getServiceStateIcon,
} from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { LabeledIconField } from '../common/components/LabeledIconField/LabeledIconField';
import { getStorageStateIconLabel } from '../common/utils';
import classes from './HostingServiceConfiguration.module.scss';
import { HostingServiceStorageConfigValidationSchema } from './StorageConfigValidationSchema';

type InfoPanelDetails = Pick<
  ServiceConfiguration,
  'id' | 'tenantId' | 'environmentId' | 'updatedDate' | 'updatedUser'
>;

export interface StorageAccountConfigData {
  provider: ProviderType;
  status: ResourceStatus | undefined;
  isStatusChangeDisabled: boolean;
}

export const HostingServiceConfiguration: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
    action: string;
  }>();

  const [serviceState, setServiceState] = useState<
    EnvironmentServiceState | undefined
  >();

  const [serviceConfigurationInfo, setServiceConfigurationInfo] =
    useState<InfoPanelDetails>();

  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCustomizableServices, setTotalCustomizableServices] = useState<
    number | undefined
  >();
  const [deployedCustomizableServices, setDeployedCustomizableServices] =
    useState<number>();
  const [storageStatus, setStorageStatus] = useState<ResourceStatus>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Get the service data
        const serviceData = await getManagedServiceData(
          envId,
          ManagedServiceId.HOSTING_SERVICE,
        );
        setServiceState(serviceData?.state);

        if (serviceData?.state !== EnvironmentServiceState.Enabled) {
          return;
        }

        // Query for existing values
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        const actualServiceConfigurationResponse =
          await envApolloClient?.query<HostingServiceConfigurationsQuery>({
            query: HostingServiceConfigurationsDocument,
            fetchPolicy: 'no-cache',
          });

        const serviceConfigurationNodes =
          actualServiceConfigurationResponse?.data?.serviceConfigurations
            ?.nodes;

        if (serviceConfigurationNodes && serviceConfigurationNodes.length > 0) {
          setServiceConfigurationInfo(serviceConfigurationNodes[0]);
        }

        // Get customizable data
        const customizableServiceResult =
          await envApolloClient?.query<CustomizableServicesQuery>({
            query: CustomizableServicesDocument,
            fetchPolicy: 'no-cache',
          });

        setTotalCustomizableServices(
          customizableServiceResult.data.filtered?.totalCount,
        );

        const storageAccountDetailsResult =
          await envApolloClient?.query<StorageAccountQuery>({
            query: StorageAccountDocument,
            fetchPolicy: 'network-only',
          });

        const storageAccountDetails =
          storageAccountDetailsResult?.data?.objectStorages?.nodes[0];

        setStorageStatus(storageAccountDetails?.status);

        const deployedServicesQueryResult =
          await envApolloClient.query<ReadyServiceDeploymentsQuery>({
            query: ReadyServiceDeploymentsDocument,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filterToPostGraphileFilter<ServiceDeploymentFilter>(
                {
                  active: true,
                  status: ResourceStatus.Ready,
                },
                { active: 'equalTo', status: 'equalTo' },
              ),
            },
          });

        setDeployedCustomizableServices(
          deployedServicesQueryResult.data.serviceDeployments?.totalCount,
        );
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const { actions } = useActions(envId);

  return (
    <Details<StorageAccountConfigData>
      defaultTitle="Hosting Service"
      subtitle="Configuration"
      validationSchema={HostingServiceStorageConfigValidationSchema}
      initialData={{
        data: undefined,
        loading,
        error,
      }}
      actions={actions}
      saveData={() => Promise.resolve()}
      infoPanel={
        <Panel
          infoPanelData={serviceConfigurationInfo}
          totalCustomizableData={totalCustomizableServices}
          deployedCustomizableServices={deployedCustomizableServices}
          storageStatus={storageStatus}
        />
      }
    >
      <Form serviceState={serviceState} />
    </Details>
  );
};

const Form: React.FC<{
  serviceState: EnvironmentServiceState | undefined;
}> = ({ serviceState }) => {
  return (
    <>
      <LabeledIconField
        name="serviceState"
        label="Status"
        icon={getServiceStateIcon(
          serviceState,
          ManagedServiceId.HOSTING_SERVICE,
        )}
        value={formatEnumValueToTitleCase(serviceState)}
      />
    </>
  );
};

const Panel: React.FC<{
  infoPanelData: InfoPanelDetails | undefined;
  totalCustomizableData: number | undefined;
  deployedCustomizableServices: number | undefined;
  storageStatus: ResourceStatus | undefined;
}> = ({
  infoPanelData,
  totalCustomizableData,
  deployedCustomizableServices,
  storageStatus,
}) => {
  const { label } = getStorageStateIconLabel(storageStatus);
  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {infoPanelData
            ? new Date(infoPanelData.updatedDate).toLocaleString() +
              ' by ' +
              infoPanelData.updatedUser
            : ''}
        </Paragraph>
      </Section>
      {config.serviceDeploymentEnabled() ? (
        <Section>
          <Paragraph title="Storage">{label}</Paragraph>
          <Paragraph title="Customizable Services">
            <div className={clsx(classes.infoPanelRow)}>
              <span>{'Deployed / Total'}</span>
              <span className={clsx(classes.colRight)}>
                {deployedCustomizableServices} / {totalCustomizableData}
              </span>
            </div>
          </Paragraph>
        </Section>
      ) : null}
      <Section>
        <Paragraph title="Tenant ID">{infoPanelData?.tenantId}</Paragraph>
        <Paragraph title="Environment ID">
          {infoPanelData?.environmentId}
        </Paragraph>
      </Section>
    </InfoPanel>
  );
};

function useActions(envId: string): {
  readonly actions: FormActionData[];
} {
  const actions: FormActionData[] = [
    {
      label: 'RabbitMQ',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/rabbitmq`,
    },
    {
      label: 'Cloud Storage',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage`,
    },
    {
      label: 'Customizable Services',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services`,
    },
  ];

  if (config.serviceDeploymentEnabled()) {
    actions.push({
      label: 'Container Registry',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/containerRegistry`,
    });
  }
  return { actions } as const;
}

export const HostingServiceConfigurationCrumb: BreadcrumbResolver = () =>
  'Hosting Service';
